import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { Modal, Button, Input } from 'components/ui';
import { useAuth } from 'hooks';

/**
 * ChangePasswordModal — lets a signed-in user set a new password without the
 * email round-trip. Re-authentication (current password) happens in
 * `authService.changePassword`; a successful change is reflected on the admin
 * Accounts page as "Set by user".
 */
export default function ChangePasswordModal({ open, onClose }) {
  const { changePassword } = useAuth();
  const [show, setShow] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { current: '', next: '', confirm: '' } });

  useEffect(() => {
    if (open) reset({ current: '', next: '', confirm: '' });
  }, [open, reset]);

  const next = watch('next');

  const submit = handleSubmit(async ({ current, next: newPassword }) => {
    try {
      await changePassword(current, newPassword);
      toast.success('Password changed');
      onClose();
    } catch (error) {
      const code = error?.code || '';
      let message;
      if (['auth/wrong-password', 'auth/invalid-credential', 'auth/invalid-login-credentials'].includes(code)) {
        message = 'Your current password is incorrect.';
      } else if (code === 'auth/weak-password') {
        message = 'New password is too weak — use at least 6 characters.';
      } else if (code === 'auth/requires-recent-login') {
        message = 'For security, please sign out and back in, then try again.';
      } else if (code === 'auth/too-many-requests') {
        message = 'Too many attempts. Please try again in a moment.';
      } else {
        message = 'Could not change your password. Please try again.';
      }
      toast.error(message);
    }
  });

  return (
    <Modal
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      title="Change password"
      description="Enter your current password, then choose a new one."
      size="sm"
      closeOnBackdrop={!isSubmitting}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={submit} loading={isSubmitting}>
            Change password
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <Input
          label="Current password"
          type={show ? 'text' : 'password'}
          autoComplete="current-password"
          error={errors.current?.message}
          {...register('current', { required: 'Enter your current password.' })}
        />
        <Input
          label="New password"
          type={show ? 'text' : 'password'}
          autoComplete="new-password"
          error={errors.next?.message}
          {...register('next', {
            required: 'Enter a new password.',
            minLength: { value: 6, message: 'Use at least 6 characters.' },
          })}
        />
        <Input
          label="Confirm new password"
          type={show ? 'text' : 'password'}
          autoComplete="new-password"
          error={errors.confirm?.message}
          {...register('confirm', {
            required: 'Re-enter your new password.',
            validate: (v) => v === next || 'Passwords do not match.',
          })}
        />
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={show}
            onChange={(e) => setShow(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          Show passwords
        </label>
      </form>
    </Modal>
  );
}
